import classNames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { css } from 'config/emotionCss';
import colors from 'config/theme/colors';
import { getDealerDefaultLanguage } from 'selectors/dealerSelectors';
import { getLanguages } from 'selectors/languageSelectors';
import Select from 'ui/base/Select';
import Input from 'ui/debounce/DebounceInput';
import TextArea from 'ui/debounce/DebounceTextArea';

import Translate from 'ui/utils/Translate';

const inputStyle = css`
  padding-right: calc(4.3rem + 8px) !important;
`;

const textAreaStyle = css`
  padding-right: 4.3rem !important;
`;

const popupClassName = css`
  width: 7rem !important;
`;

const selectStyle = css`
  z-index: 3;
  border: 0 !important;
  border-radius: 5px !important;
  background-color: #e5e7e8 !important;
  color: ${colors['@text-color-secondary']} !important;
`;

/**
 * @type {React.ForwardRefExoticComponent<{
 *   value?: Record<string, string>,
 *   onChange?: (value: Record<string, string> | React.ChangeEvent<HTMLTextAreaElement>) => void,
 *   className?: string,
 *   disabled?: boolean,
 *   selectDisabled?: boolean,
 *   useDefaultLangValueAsPlaceholder?: boolean,
 *   textarea?: boolean,
 *   readOnly?: boolean,
 *   onLanguageChange?: (language: string) => void,
 *   hideDefaultLanguageStar?: boolean,
 *   defaultLanguage?: string,
 *   'data-cy'?: string,
 * } & React.RefAttributes<{ focus: () => void }>>}
 */

/** @type {React.ForwardRefExoticComponent<TranslatableInputProps & React.RefAttributes<{ focus: () => void }>>} */
const TranslatableInput = React.forwardRef(
  (
    {
      value,
      readOnly,
      disabled,
      textarea,
      onChange,
      className,
      selectDisabled,
      onLanguageChange,
      hideDefaultLanguageStar,
      useDefaultLangValueAsPlaceholder,
      defaultLanguage: defaultLanguageProp,
      ...props
    },
    reference,
  ) => {
    /** @type {React.MutableRefObject<HTMLTextAreaElement | HTMLInputElement>} */
    const inputRef = React.useRef(null);
    const selectRef = React.useRef(null);

    const languages = useSelector(getLanguages);
    const dealerLanguage = useSelector(getDealerDefaultLanguage);

    const defaultLanguage = React.useMemo(
      () => (typeof defaultLanguageProp === 'string' ? defaultLanguageProp : dealerLanguage),
      [dealerLanguage, defaultLanguageProp],
    );

    const [selectOpen, setSelectOpen] = React.useState(false);
    const [selectedLanguage, setSelectedLanguage] = React.useState(defaultLanguage);

    const placeholder = React.useMemo(() => {
      if (useDefaultLangValueAsPlaceholder && selectedLanguage !== defaultLanguage) {
        return value?.[defaultLanguage];
      }
      return undefined;
    }, [useDefaultLangValueAsPlaceholder, selectedLanguage, defaultLanguage, value]);

    const handleChangeLanguage = React.useCallback(
      newSelectedLanguage => {
        setSelectedLanguage(newSelectedLanguage);
        onLanguageChange?.(newSelectedLanguage);
      },
      [setSelectedLanguage, onLanguageChange],
    );

    const handleOnChange = React.useCallback(
      event => {
        const textValue = event.target.value;
        const newValue = { ...value, [selectedLanguage]: textValue };
        onChange?.(newValue);
      },
      [onChange, selectedLanguage, value],
    );

    React.useImperativeHandle(reference, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
    }));

    const languageOptions = React.useMemo(
      () =>
        languages.map(language => ({
          value: language.toLowerCase(),
          'data-cy': language.toUpperCase(),
          label: <FormattedMessage id={`languageDropdown.${language}`} defaultMessage={language.toUpperCase()} />,
        })),
      [languages],
    );

    const optionRender = React.useCallback(
      ({ value: language, label }) => (
        <>
          {label}
          {!hideDefaultLanguageStar && language === defaultLanguage ? ' *' : ''}
        </>
      ),
      [defaultLanguage, hideDefaultLanguageStar],
    );

    React.useEffect(() => {
      const modalBody = document.querySelector('.ant-modal-body');
      if (!modalBody) {
        return undefined;
      }

      const handleScroll = () => {
        if (selectOpen) {
          setSelectOpen(false);
        }
      };

      modalBody.addEventListener('scroll', handleScroll);
      return () => {
        modalBody.removeEventListener('scroll', handleScroll);
      };
    }, [selectOpen]);

    return (
      <div className={classNames('position-relative', className)}>
        {readOnly && <Translate toTranslate={value} locale={selectedLanguage} />}
        {!readOnly && (
          <>
            {!textarea && (
              <Input
                disabled={disabled}
                key={selectedLanguage}
                onChange={handleOnChange}
                placeholder={placeholder}
                value={value?.[selectedLanguage]}
                {...props}
                ref={inputRef}
                classNames={{ root: inputStyle }}
              />
            )}
            {textarea && (
              <TextArea
                disabled={disabled}
                key={selectedLanguage}
                autoSize={{ minRows: 3 }}
                onChange={handleOnChange}
                placeholder={placeholder}
                value={value?.[selectedLanguage]}
                {...props}
                ref={inputRef}
                classNames={{ root: textAreaStyle }}
              />
            )}
            {/* Language selector */}
            <Select
              size="small"
              ref={selectRef}
              open={selectOpen}
              optionLabelProp="label"
              value={selectedLanguage}
              options={languageOptions}
              disabled={selectDisabled}
              data-cy="languageSelector"
              onOpenChange={setSelectOpen}
              optionRender={optionRender}
              onChange={handleChangeLanguage}
              className="position-absolute t-0 r-0 m-1 w-auto"
              classNames={{ popup: { root: popupClassName }, root: selectStyle }}
            />
          </>
        )}
      </div>
    );
  },
);

export default TranslatableInput;
